<template>
  <ui-modal-layout :title="tr('Activity details')">
    <div class="grid grid-cols-2 gap-3">
      <div class="flex flex-col gap-1">
        <span class="text-xs font-semibold text-muted-foreground">{{ tr('Date') }}</span>
        <span>{{ occurredAt }}</span>
      </div>
      <div class="flex flex-col gap-1">
        <span class="text-xs font-semibold text-muted-foreground">{{ tr('Action') }}</span>
        <span>{{ actionLabel }}</span>
      </div>
    </div>

    <section class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ tr('Automation') }}</h3>
      <router-link
        :to="`/automation/${event.rule.id}`"
        class="inline-flex w-fit items-center gap-2 text-sm font-semibold text-primary hover:underline"
        @click="() => $fullscreenModal.closeAll()"
      >
        <rule-type-badge :action="event.rule.action" compact />
        {{ event.rule.name }}
      </router-link>
    </section>

    <section class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ affectedAdsTitle }}</h3>

      <div class="flex flex-col gap-2">
        <template v-if="isLoadingAds">
          <div v-for="i in 2" :key="'ad-skel-' + i" class="glass-tile rounded-xl p-3">
            <inline-social-content />
          </div>
        </template>

        <template v-else-if="ads.length">
          <button
            v-for="affectedAd in ads"
            :key="affectedAd.id"
            type="button"
            data-slot="affected-ad"
            :class="[ 'glass-tile flex w-full cursor-pointer items-center gap-3 rounded-xl p-3 text-left', GLOW_TRANSITION, adGlow(affectedAd) ]"
            @click="() => openAd(affectedAd)"
          >
            <inline-social-content :entity="affectedAd" class="min-w-0 flex-1" />
            <chevron-right-icon class="size-5 shrink-0 text-primary" />
          </button>
        </template>

        <p v-else class="m-0 text-muted-foreground">
          {{ tr('No affected ads found.') }}
        </p>
      </div>
    </section>

    <section v-if="hasConditions" class="flex flex-col gap-1.5">
      <h3 class="m-0 text-sm font-semibold text-foreground">{{ conditionsTitle }}</h3>
      <div v-if="reasonDefinition" class="glass-tile flex flex-col gap-1 rounded-xl p-4">
        <strong>{{ tr(reasonDefinition.title) }}</strong>
        <span>{{ tr(reasonDefinition.description) }}</span>
        <span v-if="reasonExtra" class="mt-1 text-muted-foreground">
          {{ tr(reasonExtra.label) }}: {{ reasonExtra.value }}
        </span>
      </div>
      <div v-else-if="event.text" class="glass-tile rounded-xl p-4">
        {{ event.text }}
      </div>
      <condition-snapshot
        v-else
        :node="conditions"
        :fields="fields"
        :comparisons="comparisons"
        :ad-account="adAccount"
      />
    </section>
  </ui-modal-layout>
</template>

<script>
import { ChevronRight } from '@lucide/vue';

import { RULE_ACTION, RULE_ACTIVITY_REASON, RULE_ACTIVITY_REASON_DEFINITIONS } from '@/consts.js';
import RuleActivityLog from '@/entities/RuleActivityLog.js';
import RuleActivityLogService from '@/services/RuleActivityLog.js';
import AdDetailsModal from '@/ui/components/AdDetailsModal.vue';
import ConditionSnapshot from '@/ui/components/ConditionSnapshot.vue';
import InlineSocialContent from '@/ui/components/InlineSocialContent.vue';
import RuleTypeBadge from '@/ui/components/RuleTypeBadge.vue';
import { GLOW_TRANSITION, ruleActionStyle } from '@/ui/utils/ruleAction.js';
import { isConditionGroup } from '@/utils/conditions.js';
import { formatTime, formatToStringYMD } from '@/utils/date.js';

export default {
  name: 'ActivityDetailsModal',
  components: {
    ChevronRightIcon: ChevronRight,
    ConditionSnapshot,
    InlineSocialContent,
    RuleTypeBadge,
  },
  props: {
    event: {
      type: RuleActivityLog,
      required: true,
    },
  },
  data() {
    return {
      GLOW_TRANSITION,
      ads: [],
      isLoadingAds: false,
    };
  },
  computed: {
    actionLabel() {
      switch (this.event.action) {
        case RULE_ACTION.START:
          return this.tr('Ad created');

        case RULE_ACTION.STOP:
        case RULE_ACTION.SMART_STOP:
          return this.tr('Ad stopped');

        case RULE_ACTION.SCALING_UP:
          return this.tr('Ad scaled up');

        case RULE_ACTION.DELETE:
          return this.tr('Ad deleted');

        default:
          return this.event.action;
      }
    },
    occurredAt() {
      return this.formatDateAndTime(this.event.created_at);
    },
    affectedAdsTitle() {
      switch (this.event.action) {
        case RULE_ACTION.START:
        case RULE_ACTION.SCALING_UP:
          return this.ads.length === 1 ? this.tr('Created ad') : this.tr('Created ads');

        case RULE_ACTION.STOP:
        case RULE_ACTION.SMART_STOP:
          return this.ads.length === 1 ? this.tr('Stopped ad') : this.tr('Stopped ads');

        case RULE_ACTION.DELETE:
          return this.ads.length === 1 ? this.tr('Deleted ad') : this.tr('Deleted ads');

        default:
          return this.ads.length === 1 ? this.tr('Affected ad') : this.tr('Affected ads');
      }
    },
    conditions() {
      return this.event.conditions;
    },
    reasonDefinition() {
      return RULE_ACTIVITY_REASON_DEFINITIONS[this.event.reason] ?? null;
    },
    reasonExtra() {
      if (this.event.reason === RULE_ACTIVITY_REASON.EXPIRED_CONTENT && this.event.post?.ai_content_actuality) {
        return {
          label: 'Content was up to date until',
          value: this.formatDateAndTime(this.event.post.ai_content_actuality),
        };
      }

      return null;
    },
    fields() {
      return this.$store.getters['app/fields'];
    },
    comparisons() {
      return this.$store.getters['app/fieldComparisons'];
    },
    rule() {
      return this.event.rule;
    },
    adAccount() {
      return this.rule?.ad_account ?? null;
    },
    hasConditions() {
      if (this.reasonDefinition) {
        return true;
      }

      if (this.rule?.action !== this.event.action) {
        return !!this.event.text;
      }

      return isConditionGroup(this.conditions)
        && Array.isArray(this.conditions.items)
        && this.conditions.items.length > 0;
    },
    conditionsTitle() {
      switch (this.rule?.action) {
        case RULE_ACTION.STOP:
          return this.tr('Conditions for stopping the ad');

        case RULE_ACTION.START:
          return this.tr('Conditions for creating the ad');

        case RULE_ACTION.SCALING_UP:
          return this.tr('Conditions for scaling up the ad');

        default:
          return this.tr('Triggering conditions');
      }
    },
  },
  mounted() {
    this.loadAffectedAds();
  },
  methods: {
    adGlow(ad) {
      return ruleActionStyle(ad.created_by_rule?.action ?? RULE_ACTION.START).glow;
    },
    formatDateAndTime(date) {
      return `${formatToStringYMD(date, this.tr)} ${formatTime(date)}`.trim();
    },
    openAd(ad) {
      this.$fullscreenModal.open(this, AdDetailsModal, { ad });
    },
    loadAffectedAds() {
      this.isLoadingAds = true;
      RuleActivityLogService.getAffectedAds(this.event.id)
        .then(ads => {
          this.ads = ads;
          return null;
        })
        .catch(err => this.error(err))
        .finally(() => this.isLoadingAds = false);
    },
  },
};
</script>
